/**
 * CanonicalLinkEditor — the item page's connect/disconnect editor.
 *
 * PurchaseOptionsEditor handles one vendor product at a time ("move this pack
 * to that item"). This editor works at the level above: the ITEM. It answers
 * two questions a user asks while looking at an item:
 *
 *   "This and that other item are the same thing"  → Combine them. One item
 *       survives with every vendor product; the other is archived. PARs, count
 *       templates, and recipes are preserved, and the merge is undoable.
 *
 *   "This item is actually two different products" → Disconnect a vendor
 *       product into its own standalone item.
 *
 * Both actions are audited (item_cross_reference_events) and both are spelled
 * out in a confirmation that names what moves and what is archived, because
 * neither is obvious from a button label alone.
 *
 * Props:
 *   storeId         - current store id
 *   item            - the canonical inventory item being edited
 *   options         - its purchase_options rows (any status)
 *   vendorNameById? - { [vendorId]: display_name } for vendor labels
 *   onChanged?      - called after any successful mutation
 */
import React, { useCallback, useRef, useState } from 'react';
import supabase from '../config/supabase';
import { combineItems, detachOptionToNewItem } from '../services/itemCrossReferenceService';
import { fetchOptionsForItems } from '../services/purchaseOptionService';
import {
  activeOptions,
  canonicalLinkage,
  vendorLabelOf,
  comparableUnitCost,
  formatUnitPrice,
} from '../utils/itemCostSummary';

const SEARCH_LIMIT = 8;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_OPTION_LIMIT = 3;

const styles = {
  wrap: { fontFamily: 'Open Sans, sans-serif', color: '#3D2518', fontSize: 13 },
  section: {
    background: '#FBF8F5', border: '1.5px solid #E8DDD0', borderRadius: 10,
    padding: '12px 14px', marginBottom: 12,
  },
  title: { fontSize: 13, fontWeight: 700, color: '#3E2723', margin: 0 },
  hint: { fontSize: 12, color: '#8B7B73', marginTop: 3 },
  list: { listStyle: 'none', margin: '10px 0 0', padding: 0 },
  row: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 10, padding: '8px 0', borderTop: '1px solid #F1EAE2', flexWrap: 'wrap',
  },
  rowMain: { minWidth: 0, flex: '1 1 220px' },
  vendor: { fontWeight: 700, color: '#3D2518' },
  meta: { fontSize: 12, color: '#8B7B73', marginTop: 2 },
  input: {
    width: '100%', padding: '8px 10px', border: '1.5px solid #E9DFD6', borderRadius: 8,
    fontSize: 13, color: '#3D2518', background: '#fff', fontFamily: 'inherit', minHeight: 40,
  },
  label: {
    display: 'block', fontSize: 10, fontWeight: 700, color: '#5A4A42',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
  },
  result: {
    display: 'block', width: '100%', textAlign: 'left', marginTop: 5,
    padding: '8px 10px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit', minHeight: 40,
    background: '#fff', color: '#3D2518', border: '1.5px solid #E9DFD6',
  },
  resultMeta: { display: 'block', marginTop: 2, color: '#8B7B73', fontSize: 12, fontWeight: 400 },
  resultProduct: { display: 'block', marginTop: 4, color: '#5A4A42', fontSize: 12, fontWeight: 600 },
  radioRow: { display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10 },
  radio: { display: 'inline-flex', alignItems: 'flex-start', gap: 6, fontSize: 12.5, cursor: 'pointer' },
  btn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 34, padding: '6px 12px',
    borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
    background: '#fff', color: '#5A4A42', border: '1.5px solid #d5c9c0', whiteSpace: 'nowrap',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', minHeight: 38, padding: '8px 16px',
    borderRadius: 9, fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit',
    background: '#5A4A42', color: '#fff', border: 'none',
  },
  error: {
    padding: '8px 12px', color: '#b71c1c', background: '#FDECEA', borderRadius: 8,
    border: '1px solid #f5c2bd', fontSize: 12, marginBottom: 10,
  },
  notice: {
    padding: '8px 12px', color: '#2E7D32', background: '#E8F5E9', borderRadius: 8,
    border: '1px solid #A5D6A7', fontSize: 12, marginBottom: 10,
  },
  confirm: {
    background: '#FFF9EC', border: '1.5px solid #E6B866', borderRadius: 9,
    padding: '10px 12px', marginTop: 10, fontSize: 12.5, color: '#5A4A42',
  },
};

const moneyLabel = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : null;
};

const orderedSearchOptions = (item) => activeOptions(item?.purchase_options)
  .slice()
  .sort((left, right) => Number(!!right.is_preferred) - Number(!!left.is_preferred));

const searchProductDetails = (item, vendorNameById) => {
  const options = orderedSearchOptions(item);
  if (options.length > 0) {
    return {
      lines: options.slice(0, SEARCH_OPTION_LIMIT).map((option) => [
        vendorLabelOf(option, vendorNameById) || item.vendor || 'Vendor not listed',
        option.pack_description || option.purchase_unit || item.pack_size || item.unit || null,
        moneyLabel(option.package_price) || 'No price provided',
      ].filter(Boolean).join(' · ')),
      remaining: Math.max(0, options.length - SEARCH_OPTION_LIMIT),
    };
  }
  return {
    lines: [[
      item.vendor || 'Vendor not listed',
      item.pack_size || item.unit || null,
      moneyLabel(item.case_price ?? item.unit_price),
    ].filter(Boolean).join(' · ')],
    remaining: 0,
  };
};

export default function CanonicalLinkEditor({
  storeId,
  item,
  options,
  vendorNameById = {},
  onChanged,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [survivor, setSurvivor] = useState('this'); // 'this' | 'other'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const timerRef = useRef(null);

  const linked = activeOptions(options);
  const linkage = canonicalLinkage(options);

  const runSearch = useCallback(async (raw) => {
    const text = raw.trim();
    if (!text || !storeId) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const pattern = `%${text.replace(/[%_\\]/g, '')}%`;
      const { data, error: searchError } = await supabase
        .from('inventory_items')
        .select('id, description, brand, category, unit, vendor, pack_size, case_price, unit_price, is_active, deleted_at')
        .eq('store_id', storeId)
        .not('is_active', 'is', false)
        .is('deleted_at', null)
        .ilike('description', pattern)
        .order('description')
        .limit(SEARCH_LIMIT + 1);
      if (searchError) throw new Error(searchError.message);
      const liveRows = (data || [])
        .filter((row) => String(row.id) !== String(item?.id))
        .slice(0, SEARCH_LIMIT);
      let optionsByItemId = {};
      try {
        optionsByItemId = await fetchOptionsForItems(storeId, liveRows.map((row) => row.id));
      } catch (optionError) {
        // The item result is still useful when option enrichment is
        // temporarily unavailable; its legacy vendor/unit/price fields are
        // rendered as a fallback.
        console.warn('[related-products] Could not enrich item search results:', optionError?.message);
      }
      setResults(liveRows.map((row) => ({
        ...row,
        purchase_options: optionsByItemId[row.id] || [],
      })));
      setError('');
    } catch (err) {
      setResults([]);
      setError(err?.message || 'Item search failed.');
    } finally {
      setSearching(false);
    }
  }, [storeId, item?.id]);

  const handleQueryChange = (value) => {
    setQuery(value);
    setTarget(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(value), SEARCH_DEBOUNCE_MS);
  };

  const resetCombine = () => {
    setQuery('');
    setResults([]);
    setTarget(null);
    setSurvivor('this');
  };

  const handleCombine = async () => {
    if (!target || busy) return;
    const keepThis = survivor === 'this';
    const keptName = keepThis ? (item?.description || 'this item') : (target.description || 'the other item');
    const archivedName = keepThis ? (target.description || 'the other item') : (item?.description || 'this item');
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await combineItems(
        storeId,
        keepThis ? target.id : item.id,
        keepThis ? item.id : target.id,
        { channel: 'editor', reason: 'manual:item-editor' },
      );
      setNotice(`Combined. "${keptName}" now carries every vendor product; "${archivedName}" was archived.`);
      resetCombine();
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      setError(err?.message || 'Could not combine these items.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async (option) => {
    const label = vendorLabelOf(option, vendorNameById) || option.vendor_description || 'this vendor product';
    if (!window.confirm(
      `Disconnect ${label} from "${item?.description || 'this item'}"?\n\n`
      + 'A new standalone item is created and this vendor product moves to it. '
      + 'Counts, PARs, and recipes on this item are untouched.',
    )) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await detachOptionToNewItem(option.id);
      setNotice(`${label} is now its own item.`);
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      setError(err?.message || 'Could not disconnect that vendor product.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.wrap} data-testid="canonical-link-editor">
      {error && <div style={styles.error} role="alert">{error}</div>}
      {notice && <div style={styles.notice} role="status">{notice}</div>}

      <section style={styles.section}>
        <h4 style={styles.title}>Connected vendor products</h4>
        <p style={styles.hint}>
          {linkage.isCanonical
            ? `${linkage.headingLabel}. Counting stays in ${item?.unit || 'the item unit'} whichever one you buy.`
            : 'Only one way to buy this item so far.'}
        </p>
        {linked.length === 0 ? (
          <p style={styles.hint}>No vendor products linked yet.</p>
        ) : (
          <ul style={styles.list}>
            {linked.map((option) => {
              const unitCost = comparableUnitCost(option);
              return (
                <li key={option.id} style={styles.row} data-testid="cle-linked-option">
                  <div style={styles.rowMain}>
                    <span style={styles.vendor}>
                      {vendorLabelOf(option, vendorNameById) || 'Vendor not linked'}
                    </span>
                    <div style={styles.meta}>
                      {[
                        option.vendor_description || null,
                        option.pack_description || option.purchase_unit || null,
                        unitCost === null
                          ? 'unit price unavailable'
                          : formatUnitPrice(unitCost, item?.unit),
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={styles.btn}
                    disabled={busy}
                    onClick={() => handleDisconnect(option)}
                  >
                    Disconnect into its own item
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section style={styles.section}>
        <h4 style={styles.title}>Combine with another item</h4>
        <p style={styles.hint}>
          Use this when two rows in the catalog are really the same product. One item keeps
          every vendor product; the other is archived. Counts, PARs, templates, and recipes
          are preserved, and the change can be undone from the Related Products history.
        </p>

        <div style={{ marginTop: 10 }}>
          <label style={styles.label} htmlFor="cle-search">Find the other item</label>
          <input
            id="cle-search"
            type="search"
            style={styles.input}
            placeholder="Search items by name…"
            value={query}
            onChange={(event) => handleQueryChange(event.target.value)}
          />
        </div>

        {searching && <div style={styles.hint}>Searching…</div>}
        {!searching && query.trim() !== '' && results.length === 0 && (
          <div style={styles.hint}>No matching items.</div>
        )}
        {!target && results.map((row) => {
          const productDetails = searchProductDetails(row, vendorNameById);
          return (
            <button
              key={row.id}
              type="button"
              style={styles.result}
              data-testid="cle-search-result"
              onClick={() => setTarget(row)}
            >
              {row.description || `Item ${row.id}`}
              {[row.brand, row.category, row.unit ? `counted in ${row.unit}` : null]
                .filter(Boolean).length > 0 && (
                <span style={styles.resultMeta}>
                  {[row.brand, row.category, row.unit ? `counted in ${row.unit}` : null]
                    .filter(Boolean).join(' · ')}
                </span>
              )}
              {productDetails.lines.map((line, index) => (
                <span key={`${row.id}-product-${index}`} style={styles.resultProduct}>{line}</span>
              ))}
              {productDetails.remaining > 0 && (
                <span style={styles.resultMeta}>
                  +{productDetails.remaining} more vendor product{productDetails.remaining === 1 ? '' : 's'}
                </span>
              )}
            </button>
          );
        })}

        {target && (
          <div style={styles.confirm} data-testid="cle-confirm">
            <div>
              Combining <strong>{item?.description || 'this item'}</strong> and{' '}
              <strong>{target.description || `item ${target.id}`}</strong>.
            </div>
            <div style={styles.radioRow} role="radiogroup" aria-label="Which item survives">
              <label style={styles.radio}>
                <input
                  type="radio"
                  name="cle-survivor"
                  value="this"
                  checked={survivor === 'this'}
                  onChange={() => setSurvivor('this')}
                />
                <span>
                  Keep <strong>{item?.description || 'this item'}</strong>
                  <br />
                  <span style={styles.meta}>
                    {target.description || 'The other item'} is archived; its vendor products move here.
                  </span>
                </span>
              </label>
              <label style={styles.radio}>
                <input
                  type="radio"
                  name="cle-survivor"
                  value="other"
                  checked={survivor === 'other'}
                  onChange={() => setSurvivor('other')}
                />
                <span>
                  Keep <strong>{target.description || `item ${target.id}`}</strong>
                  <br />
                  <span style={styles.meta}>
                    {item?.description || 'This item'} is archived; its vendor products move there.
                  </span>
                </span>
              </label>
            </div>
            {item?.unit && target.unit && item.unit !== target.unit && (
              <div style={{ ...styles.meta, marginTop: 8, color: '#8A5A00', fontWeight: 700 }}>
                These items count in different units ({item.unit} vs {target.unit}). The surviving
                item keeps its own unit — check the vendor products&apos; pack conversions afterwards.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button type="button" style={styles.primaryBtn} disabled={busy} onClick={handleCombine}>
                {busy ? 'Combining…' : 'Combine items'}
              </button>
              <button type="button" style={styles.btn} disabled={busy} onClick={resetCombine}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
